/**
 * Create Production Batch Modal Component
 * 2-step wizard for creating new production batches
 *
 * @author SAUBHAGYA Development Team
 * @since Story 5-5
 *
 * @description
 * Modal wizard component with:
 * - Step 1: Select final product type
 * - Step 2: Select raw materials with quantities
 * - Material selection list with available quantities
 * - Summary of selected materials
 * - Notes textarea
 * - Cancel confirmation if materials selected
 *
 * @example
 * ```tsx
 * const [open, setOpen] = useState(false);
 *
 * <CreateProductionBatchModal
 *   open={open}
 *   onOpenChange={setOpen}
 *   onSuccess={(batch) => console.log('Batch created:', batch)}
 * />
 * ```
 */

import React, { useState } from 'react';
import { Loader2, ChevronRight, ChevronLeft, Package, Plus, X } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';

import { useCreateProductionBatch, useAvailableTransactions } from '@/hooks/useProductionBatches';
import { useDungProducts } from '@/hooks/useMasterData';
import { ProductionBatchDTO } from '@/services/manufacturing/manufacturingApi';
import { toast } from 'sonner';

interface CreateProductionBatchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (batch: ProductionBatchDTO) => void;
}

interface SelectedMaterial {
  transactionId: number;
  batchCode: string;
  rawMaterialTypeName: string;
  supplierName: string;
  availableQuantityKg: number;
  quantityKg: number;
}

/**
 * Create Production Batch Modal Component
 */
export function CreateProductionBatchModal({
  open,
  onOpenChange,
  onSuccess,
}: CreateProductionBatchModalProps) {
  const [step, setStep] = useState<1 | 2>(1);

  // Step 1: Dung Product Selection
  const [dungProductId, setDungProductId] = useState<string>('');

  // Step 2: Material Selection
  const [selectedMaterials, setSelectedMaterials] = useState<SelectedMaterial[]>([]);
  const [notes, setNotes] = useState('');

  // Track raw input values for quantity fields (allows clearing/editing)
  const [inputValues, setInputValues] = useState<Record<number, string>>({});

  // Fetch master data
  const { data: dungProducts = [] } = useDungProducts();

  // Fetch available transactions when unit is selected
  const { data: availableTransactions = [], isLoading: isLoadingTransactions } =
    useAvailableTransactions(
      undefined,
      step === 2
    );

  // Create batch mutation
  const createBatch = useCreateProductionBatch();

  // Calculate total selected quantity
  const totalSelectedQuantity = selectedMaterials.reduce(
    (sum, material) => sum + material.quantityKg,
    0
  );

  // Handle material addition
  const handleAddMaterial = (
    transactionId: number,
    quantityKg: number
  ) => {
    const transaction = availableTransactions.find((t) => t.id === transactionId);
    if (!transaction) return;

    const existingIndex = selectedMaterials.findIndex(
      (m) => m.transactionId === transactionId
    );

    if (existingIndex >= 0) {
      // Update existing material
      const updated = [...selectedMaterials];
      updated[existingIndex] = {
        ...updated[existingIndex],
        quantityKg,
      };
      setSelectedMaterials(updated);
    } else {
      // Add new material
      setSelectedMaterials([
        ...selectedMaterials,
        {
          transactionId,
          batchCode: transaction.batchCode,
          rawMaterialTypeName: transaction.rawMaterialTypeName,
          supplierName: transaction.supplierName,
          availableQuantityKg: transaction.availableQuantityKg,
          quantityKg,
        },
      ]);
    }
  };

  // Handle material removal
  const handleRemoveMaterial = (transactionId: number) => {
    setSelectedMaterials(
      selectedMaterials.filter((m) => m.transactionId !== transactionId)
    );
    // Clean up raw input value
    setInputValues((prev) => {
      const next = { ...prev };
      delete next[transactionId];
      return next;
    });
  };

  // Handle next step
  const handleNextStep = () => {
    if (step === 1) {
      if (!dungProductId) {
        toast.error('Please select a dung product');
        return;
      }
      setStep(2);
    }
  };

  // Handle previous step
  const handlePreviousStep = () => {
    if (step === 2) {
      setStep(1);
    }
  };

  // Handle submit
  const handleSubmit = async () => {
    if (selectedMaterials.length === 0) {
      toast.error('Please select at least one material');
      return;
    }

    try {
      const result = await createBatch.mutateAsync({
        // manufacturingUnitId auto-assigned from JWT
        dungProductId: parseInt(dungProductId),
        inputs: selectedMaterials.map((m) => ({
          transactionId: m.transactionId,
          quantityKg: m.quantityKg,
        })),
        notes: notes || undefined,
      });

      toast.success(`Production batch ${result.batchCode} created successfully`);

      onSuccess?.(result);
      handleClose();
    } catch (error: any) {
      toast.error(error.message || 'Failed to create production batch');
    }
  };

  // Handle close with confirmation
  const handleClose = () => {
    if (selectedMaterials.length > 0) {
      if (
        !confirm(
          'You have selected materials. Are you sure you want to cancel?'
        )
      ) {
        return;
      }
    }

    // Reset form
    setStep(1);
    setDungProductId('');
    setSelectedMaterials([]);
    setInputValues({});
    setNotes('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Production Batch</DialogTitle>
          <DialogDescription>
            Step {step} of 2:{' '}
            {step === 1
              ? 'Select dung product'
              : 'Select raw materials'}
            <span className="block text-xs text-muted-foreground mt-1">
              Fields marked with <span className="text-red-500">*</span> are mandatory
            </span>
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: Dung Product Selection */}
        {step === 1 && (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="dung-product">
                <Package className="inline h-4 w-4 mr-1" />
                Dung Product
                <span className="text-red-500 ml-0.5">*</span>
              </Label>
              <Select
                value={dungProductId}
                onValueChange={setDungProductId}
              >
                <SelectTrigger id="dung-product">
                  <SelectValue placeholder="Select dung product" />
                </SelectTrigger>
                <SelectContent>
                  {dungProducts.map((product) => (
                    <SelectItem key={product.id} value={product.id.toString()}>
                      {product.productName} {product.unit ? `(${product.unit})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Step 2: Material Selection */}
        {step === 2 && (
          <div className="space-y-4 py-4">
            {/* Available Materials */}
            <div>
              <Label className="mb-2 block">
                Available Materials
                <span className="text-red-500 ml-0.5">*</span>
              </Label>
              {isLoadingTransactions ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : availableTransactions.length === 0 ? (
                <Alert>
                  <AlertDescription>
                    No approved materials available for your manufacturing unit.
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto border rounded-lg p-2">
                  {availableTransactions.map((transaction) => {
                    const selected = selectedMaterials.find(
                      (m) => m.transactionId === transaction.id
                    );
                    const isSelected = !!selected;

                    return (
                      <Card key={transaction.id} className={isSelected ? 'border-primary' : ''}>
                        <CardContent className="pt-4">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 text-sm">
                              <p className="font-medium">
                                {transaction.rawMaterialTypeName}
                              </p>
                              <p className="text-muted-foreground text-xs">
                                Batch: {transaction.batchCode} | Supplier:{' '}
                                {transaction.supplierName}
                              </p>
                              <p className="text-xs mt-1">
                                Available: {transaction.availableQuantityKg} kg | Grade:{' '}
                                {transaction.qcGrade}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Input
                                id={`qty-${transaction.id}`}
                                name={`qty-${transaction.id}`}
                                type="text"
                                inputMode="numeric"
                                placeholder="Qty (kg)"
                                className="w-28"
                                value={
                                  inputValues[transaction.id] !== undefined
                                    ? inputValues[transaction.id]
                                    : (selected?.quantityKg ? String(selected.quantityKg) : '')
                                }
                                onChange={(e) => {
                                  const rawValue = e.target.value;

                                  // Allow clearing the field
                                  if (rawValue === '') {
                                    setInputValues((prev) => ({ ...prev, [transaction.id]: '' }));
                                    // Remove material when field is cleared
                                    if (isSelected) {
                                      handleRemoveMaterial(transaction.id);
                                    }
                                    return;
                                  }

                                  // Only allow whole numbers (digits only)
                                  const filtered = rawValue.replace(/[^0-9]/g, '');
                                  if (filtered === '') return;

                                  // Remove leading zeros
                                  const cleanValue = String(parseInt(filtered, 10));
                                  const numValue = parseInt(cleanValue, 10);
                                  const maxQty = Math.floor(transaction.availableQuantityKg);

                                  // Enforce max limit
                                  if (numValue > maxQty) {
                                    setInputValues((prev) => ({ ...prev, [transaction.id]: String(maxQty) }));
                                    handleAddMaterial(transaction.id, maxQty);
                                    return;
                                  }

                                  setInputValues((prev) => ({ ...prev, [transaction.id]: cleanValue }));

                                  if (numValue > 0) {
                                    handleAddMaterial(transaction.id, numValue);
                                  }
                                }}
                                onBlur={() => {
                                  // On blur, sync display with stored value
                                  setInputValues((prev) => {
                                    const next = { ...prev };
                                    delete next[transaction.id];
                                    return next;
                                  });
                                }}
                              />
                              {isSelected ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemoveMaterial(transaction.id)}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const defaultQty = Math.min(
                                      Math.floor(transaction.availableQuantityKg),
                                      100
                                    );
                                    handleAddMaterial(transaction.id, defaultQty);
                                  }}
                                >
                                  <Plus className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>

            <Separator />

            {/* Selected Materials Summary */}
            {selectedMaterials.length > 0 && (
              <div>
                <Label className="mb-2 block">
                  Selected Materials ({selectedMaterials.length})
                </Label>
                <Card>
                  <CardContent className="pt-4">
                    <div className="space-y-2">
                      {selectedMaterials.map((material) => (
                        <div
                          key={material.transactionId}
                          className="flex items-center justify-between text-sm"
                        >
                          <div>
                            <span className="font-medium">
                              {material.rawMaterialTypeName}
                            </span>
                            <span className="text-muted-foreground text-xs ml-2">
                              ({material.batchCode})
                            </span>
                          </div>
                          <span className="font-medium">{material.quantityKg} kg</span>
                        </div>
                      ))}
                      <Separator />
                      <div className="flex items-center justify-between font-semibold">
                        <span>Total Input Quantity</span>
                        <span>{totalSelectedQuantity.toFixed(2)} kg</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                placeholder="Add any notes about this production batch..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          {step === 1 ? (
            <>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleNextStep}>
                Next
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={handlePreviousStep}>
                <ChevronLeft className="mr-2 h-4 w-4" />
                Previous
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={createBatch.isPending || selectedMaterials.length === 0}
              >
                {createBatch.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Create Batch
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CreateProductionBatchModal;
